/**
 * DocumentRequestsTab — Onglet "Demande de documents" sur la fiche employé.
 *
 * P5-A : permet de créer/suivre/relancer des demandes de documents envoyées au
 * candidat par magic-link. P5-B : page candidat publique pour upload.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Mail,
  Plus,
  Send,
  RefreshCw,
  Trash2,
  Check,
  X,
  Eye,
  Clock,
  CheckCircle2,
  AlertCircle,
  Copy,
  Loader,
  Sparkles,
  PlusCircle,
  Bookmark,
} from 'lucide-react';
import Modal from '../ui/Modal';
import HRDocViewer from './HRDocViewer';
import api, {
  listDocumentRequests,
  createDocumentRequest,
  resendDocumentRequest,
  regenerateDocumentRequestLink,
  deleteDocumentRequest,
  updateDocumentRequestItem,
  addDocumentRequestItems,
  promoteDocumentRequestItem,
  getDocumentRequestEmailPresets,
  getEmployeeDocumentTypes,
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';
import LoadingSpinner from '../LoadingSpinner';

const STATUS_BADGE = {
  draft:        { label: 'Brouillon',    bg: 'bg-gray-100',   text: 'text-gray-700',   icon: Clock },
  sent:         { label: 'Envoyée',      bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Send },
  in_progress:  { label: 'En cours',     bg: 'bg-amber-100',  text: 'text-amber-700',  icon: Clock },
  completed:    { label: 'Complète',     bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle2 },
  cancelled:    { label: 'Annulée',      bg: 'bg-red-100',    text: 'text-red-700',    icon: X },
};

const ITEM_STATUS_BADGE = {
  pending:    { label: 'En attente',  bg: 'bg-gray-100',  text: 'text-gray-700' },
  uploaded:   { label: 'Envoyé',      bg: 'bg-amber-100', text: 'text-amber-800' },
  validated:  { label: 'Validé',      bg: 'bg-green-100', text: 'text-green-700' },
  rejected:   { label: 'Refusé',      bg: 'bg-red-100',   text: 'text-red-700' },
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function DocumentRequestsTab({ employee }) {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null); // requestId déplié
  const [freshLinks, setFreshLinks] = useState({}); // { requestId: portalUrl } — perdu au refresh
  const [viewingDoc, setViewingDoc] = useState(null); // { id, fileName }
  const [addItemsTarget, setAddItemsTarget] = useState(null); // requestId
  const [docTypesAll, setDocTypesAll] = useState([]); // cache pour le modal "ajouter une pièce"

  useEffect(() => {
    getEmployeeDocumentTypes().then((r) => setDocTypesAll(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listDocumentRequests(employee.id);
      setRequests(data || []);
    } catch (e) {
      toast.error('Erreur chargement demandes');
    } finally {
      setLoading(false);
    }
  }, [employee.id, toast]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (id) => {
    if (!window.confirm('Régénérer le lien (l\'ancien sera invalidé) et renvoyer l\'email à l\'employé ?')) return;
    try {
      const { data } = await resendDocumentRequest(id);
      if (data.portalUrl) {
        setFreshLinks((s) => ({ ...s, [id]: data.portalUrl }));
        setExpanded(id);
      }
      if (data.mailError) toast.error('Email non envoyé : ' + data.mailError);
      else toast.success('Lien régénéré et email envoyé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur renvoi');
    }
  };

  const handleRegenerateLink = async (id) => {
    if (!window.confirm('Régénérer le lien sans envoyer d\'email ? L\'ancien lien sera invalidé immédiatement.')) return;
    try {
      const { data } = await regenerateDocumentRequestLink(id);
      if (data.portalUrl) {
        setFreshLinks((s) => ({ ...s, [id]: data.portalUrl }));
        setExpanded(id);
      }
      toast.success('Lien régénéré');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur régénération');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ? Les documents déjà uploadés restent dans la fiche.')) return;
    try {
      await deleteDocumentRequest(id);
      toast.success('Demande supprimée');
      load();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleValidateItem = async (itemId) => {
    try {
      await updateDocumentRequestItem(itemId, { action: 'validate' });
      toast.success('Document validé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur validation');
    }
  };

  const handleRejectItem = async (itemId) => {
    const reason = window.prompt('Motif du refus (sera visible par le candidat) :');
    if (!reason) return;
    try {
      await updateDocumentRequestItem(itemId, { action: 'reject', rejectedReason: reason });
      toast.success('Document refusé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur refus');
    }
  };

  const handlePromoteItem = async (item) => {
    const category = window.prompt(
      `Enregistrer "${item.customLabel}" comme type permanent ?\nCatégorie (optionnel) :`,
      item.customCategory || ''
    );
    if (category === null) return; // Annulé
    try {
      const { data } = await promoteDocumentRequestItem(item.id, {
        category: category || undefined,
        mandatory: false,
      });
      toast.success(`Type "${data.type?.name}" créé et réutilisable`);
      // Recharge la liste des types et la liste des demandes
      getEmployeeDocumentTypes().then((r) => setDocTypesAll(r.data || [])).catch(() => {});
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur promotion');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><LoadingSpinner size="default" text="Chargement..." inline /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-gray-800">Demandes de documents</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Envoie au candidat un lien sécurisé pour qu'il transmette ses documents (CI, RIB, diplômes, etc.).
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-2"
        >
          <Plus size={14} /> Nouvelle demande
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-sm text-gray-500">
          <Mail className="mx-auto mb-3 text-gray-400" size={28} />
          <p>Aucune demande pour cet employé.</p>
          <p className="text-xs mt-1">Crée une nouvelle demande pour collecter les pièces nécessaires à l'embauche.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const sb = STATUS_BADGE[req.status] || STATUS_BADGE.draft;
            const Icon = sb.icon;
            const total = req.items.length;
            const validated = req.items.filter(i => i.status === 'validated').length;
            const uploaded = req.items.filter(i => i.status === 'uploaded').length;
            const isOpen = expanded === req.id;
            return (
              <div key={req.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(isOpen ? null : req.id)}>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sb.bg} ${sb.text}`}>
                    <Icon size={12} /> {sb.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{req.emailSubject || 'Demande de documents'}</div>
                    <div className="text-xs text-gray-500 truncate">
                      Envoyée à <span className="font-medium">{req.recipientEmail}</span> · Créée le {fmtDateTime(req.createdAt)}
                      {req.sentAt ? ` · Envoyée le ${fmtDateTime(req.sentAt)}` : ''}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600 whitespace-nowrap">
                    <div className="font-semibold text-gray-800">{validated}/{total} validés</div>
                    {uploaded > 0 && <div className="text-amber-700">{uploaded} à valider</div>}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-200">
                    <div className="px-4 py-3 bg-gray-50 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleRegenerateLink(req.id)}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
                        title="Génère un nouveau lien sans envoyer d'email"
                      >
                        <RefreshCw size={12} /> Régénérer le lien
                      </button>
                      <button
                        onClick={() => handleResend(req.id)}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
                        title="Régénère un nouveau lien et renvoie l'email"
                      >
                        <Mail size={12} /> Régénérer + envoyer par email
                      </button>
                      <button
                        onClick={() => setAddItemsTarget(req.id)}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
                        title="Ajouter une pièce à cette demande"
                      >
                        <PlusCircle size={12} /> Ajouter une pièce
                      </button>
                      <button
                        onClick={() => handleDelete(req.id)}
                        className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1.5"
                      >
                        <Trash2 size={12} /> Supprimer
                      </button>
                      {req.notes && (
                        <div className="ml-auto text-xs text-gray-600 italic">{req.notes}</div>
                      )}
                    </div>

                    {freshLinks[req.id] && (
                      <MagicLinkBox
                        url={freshLinks[req.id]}
                        onDismiss={() => setFreshLinks((s) => { const c = { ...s }; delete c[req.id]; return c; })}
                      />
                    )}
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 text-xs text-gray-600">
                        <tr>
                          <th className="text-left px-4 py-2">Document</th>
                          <th className="text-left px-4 py-2">Statut</th>
                          <th className="text-left px-4 py-2">Reçu</th>
                          <th className="text-right px-4 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {req.items.map((it) => {
                          const ib = ITEM_STATUS_BADGE[it.status] || ITEM_STATUS_BADGE.pending;
                          return (
                            <tr key={it.id} className="border-t border-gray-100">
                              <td className="px-4 py-2.5">
                                <div className="font-medium text-gray-800 flex items-center gap-1.5">
                                  {it.documentType?.name || it.customLabel || '—'}
                                  {!it.documentType && it.customLabel && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200 flex items-center gap-0.5">
                                      <Sparkles size={9} /> Personnalisé
                                    </span>
                                  )}
                                </div>
                                {(it.customCategory || it.documentType?.category) && (
                                  <span className="text-[10px] text-gray-400">{it.customCategory || it.documentType?.category}</span>
                                )}
                                {it.required && <span className="text-[10px] text-red-600 ml-1">Obligatoire</span>}
                                {it.rejectedReason && (
                                  <div className="text-xs text-red-700 mt-1 flex items-start gap-1">
                                    <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                                    {it.rejectedReason}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${ib.bg} ${ib.text}`}>{ib.label}</span>
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-600">{it.uploadedAt ? fmtDateTime(it.uploadedAt) : '—'}</td>
                              <td className="px-4 py-2.5 text-right">
                                <div className="inline-flex gap-1.5 items-center">
                                  {it.employeeDocumentId && (
                                    <button
                                      className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 flex items-center gap-1"
                                      title="Visualiser le document"
                                      onClick={() => setViewingDoc({
                                        id: it.employeeDocumentId,
                                        fileName: it.employeeDocument?.originalFileName
                                          || it.employeeDocument?.fileName
                                          || `${it.documentType?.name || it.customLabel || 'document'}.pdf`,
                                      })}
                                    >
                                      <Eye size={12} /> Voir
                                    </button>
                                  )}
                                  {!it.documentType && it.customLabel && (
                                    <button
                                      className="px-2 py-1 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200 flex items-center gap-1"
                                      title="Enregistrer ce document comme type permanent (réutilisable)"
                                      onClick={() => handlePromoteItem(it)}
                                    >
                                      <Bookmark size={12} /> Type permanent
                                    </button>
                                  )}
                                  {it.status === 'uploaded' && (
                                    <>
                                      <button
                                        className="px-2 py-1 text-xs text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 flex items-center gap-1"
                                        title="Valider"
                                        onClick={() => handleValidateItem(it.id)}
                                      >
                                        <Check size={12} /> Valider
                                      </button>
                                      <button
                                        className="px-2 py-1 text-xs text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200 flex items-center gap-1"
                                        title="Refuser"
                                        onClick={() => handleRejectItem(it.id)}
                                      >
                                        <X size={12} /> Refuser
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {viewingDoc && (
        <HRDocViewer
          key={viewingDoc.id}
          fileName={viewingDoc.fileName}
          fetchBlob={() => api.get(`/hr/employees/documents/${viewingDoc.id}/file`, { responseType: 'blob' })}
          onClose={() => setViewingDoc(null)}
        />
      )}

      {addItemsTarget && (
        <AddItemsModal
          requestId={addItemsTarget}
          docTypes={docTypesAll}
          existingItems={requests.find((r) => r.id === addItemsTarget)?.items || []}
          onClose={() => setAddItemsTarget(null)}
          onAdded={() => { setAddItemsTarget(null); load(); }}
        />
      )}

      {showCreate && (
        <CreateRequestModal
          employee={employee}
          onClose={() => setShowCreate(false)}
          onCreated={({ requestId, portalUrl }) => {
            setShowCreate(false);
            if (requestId && portalUrl) {
              setFreshLinks((s) => ({ ...s, [requestId]: portalUrl }));
              setExpanded(requestId);
            }
            load();
          }}
        />
      )}
    </div>
  );
}

// ─── Boîte d'affichage du magic-link (visible 1 seule fois) ─────────────

function MagicLinkBox({ url, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Fallback ancien navigateur
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
      <div className="flex items-start gap-2">
        <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-amber-900 mb-1.5">
            Lien généré — il ne sera affiché qu'une seule fois. Copie-le maintenant si tu veux le partager autrement que par email.
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-white border border-amber-300 rounded px-2 py-1.5 text-xs font-mono text-gray-800 focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
            <button
              onClick={handleCopy}
              className="px-3 py-1.5 text-xs bg-white border border-amber-400 text-amber-800 rounded hover:bg-amber-100 flex items-center gap-1.5"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copié' : 'Copier'}
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1.5 text-amber-700 hover:bg-amber-100 rounded"
                title="Masquer (le lien sera perdu)"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modale création d'une demande ──────────────────────────────────────

function CreateRequestModal({ employee, onClose, onCreated }) {
  const toast = useToast();
  const [docTypes, setDocTypes] = useState([]);
  const [selected, setSelected] = useState({}); // { typeId: { required: bool, checked: bool } }
  const [customItems, setCustomItems] = useState([]); // [{ label, required, category }]
  const [email, setEmail] = useState(employee?.email || '');
  const [recipientName, setRecipientName] = useState(`${employee?.firstName || ''} ${employee?.lastName || ''}`.trim());
  const [submitting, setSubmitting] = useState(false);

  // Email scénario + presets éditables
  const [presets, setPresets] = useState({});      // { embauche: {subject, body}, complement: ..., mise_a_jour: ... }
  const [scenario, setScenario] = useState('embauche');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);    // empêche d'écraser une saisie manuelle si l'admin change de scénario
  const [subjectEdited, setSubjectEdited] = useState(false);

  useEffect(() => {
    getEmployeeDocumentTypes()
      .then((r) => {
        const types = r.data || [];
        setDocTypes(types);
        // Pré-coche les mandatory + Set required par défaut
        const init = {};
        types.forEach((t) => {
          init[t.id] = { checked: !!t.mandatory, required: !!t.mandatory };
        });
        setSelected(init);
      })
      .catch(() => setDocTypes([]));

    // Charge les presets mail
    getDocumentRequestEmailPresets(employee.id)
      .then((r) => {
        setPresets(r.data || {});
        const p = (r.data || {})['embauche'] || {};
        setEmailSubject(p.subject || '');
        setEmailBody(p.body || '');
      })
      .catch(() => {});
  }, [employee.id]);

  // Change de scénario : remplace l'objet et le corps SAUF si l'admin a déjà édité
  const handleChangeScenario = (key) => {
    setScenario(key);
    const p = presets[key] || {};
    if (!subjectEdited) setEmailSubject(p.subject || '');
    if (!bodyEdited) setEmailBody(p.body || '');
  };

  const grouped = useMemo(() => {
    const map = new Map();
    docTypes.forEach((t) => {
      const cat = t.category || 'Autres';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [docTypes]);

  const toggleChecked = (id) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id], checked: !s[id]?.checked } }));
  const toggleRequired = (id) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id], required: !s[id]?.required } }));

  const checkedIds = Object.entries(selected).filter(([, v]) => v.checked).map(([id]) => id);

  const cleanedCustom = customItems
    .map((it) => ({ ...it, label: String(it.label || '').trim() }))
    .filter((it) => it.label.length > 0);

  const handleSubmit = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Email destinataire invalide');
      return;
    }
    if (checkedIds.length === 0 && cleanedCustom.length === 0) {
      toast.error('Sélectionne au moins un document ou ajoute un document personnalisé');
      return;
    }
    setSubmitting(true);
    try {
      const requiredMap = {};
      checkedIds.forEach((id) => { requiredMap[id] = selected[id]?.required !== false; });

      const { data } = await createDocumentRequest({
        employeeId: employee.id,
        recipientEmail: email.trim(),
        recipientName: recipientName.trim() || undefined,
        documentTypeIds: checkedIds,
        required: requiredMap,
        customItems: cleanedCustom,
        emailSubject: emailSubject?.trim() || undefined,
        emailBody: emailBody?.trim() || undefined,
        sendNow: true,
      });
      if (data.mailError) {
        toast.error('Demande créée mais email non envoyé : ' + data.mailError);
      } else {
        toast.success('Demande créée et email envoyé');
      }
      onCreated({ requestId: data.request?.id, portalUrl: data.portalUrl });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur création demande');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Nouvelle demande de documents"
      size="2xl"
      footer={
        <>
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-60 flex items-center gap-2"
          >
            {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? 'Envoi...' : 'Créer et envoyer'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
          <Mail size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-0.5">Demande pour {employee?.firstName} {employee?.lastName}</p>
            <p>L'employé recevra un email avec un lien sécurisé (valable 30 jours). Un code de vérification lui sera envoyé par email avant d'accéder au portail. Le texte de l'email utilise le modèle par défaut Ô P'tites Quenottes (éditable dans la liste après création).</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Email destinataire <span className="text-red-500">*</span></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="prenom.nom@email.com" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Nom destinataire</label>
            <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className={inputCls} placeholder="Vanessa ARNOULD" />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Documents demandés</h4>
            <div className="text-xs text-gray-500">{checkedIds.length + cleanedCustom.length} sélectionné(s)</div>
          </div>
          {docTypes.length === 0 ? (
            <div className="text-xs text-gray-500 italic">Aucun type configuré (Admin &gt; Types de documents employé)</div>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {grouped.map(([cat, items]) => (
                <div key={cat}>
                  <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">{cat}</div>
                  {items.map((t) => {
                    const sel = selected[t.id] || {};
                    return (
                      <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={!!sel.checked} onChange={() => toggleChecked(t.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-800 truncate">{t.name}</div>
                        </div>
                        <label
                          className={`text-[10px] px-2 py-0.5 rounded cursor-pointer ${sel.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (sel.checked) toggleRequired(t.id); }}
                          title={sel.required ? 'Obligatoire — clic pour rendre optionnel' : 'Optionnel — clic pour rendre obligatoire'}
                        >
                          {sel.required ? 'Obligatoire' : 'Optionnel'}
                        </label>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <CustomItemsEditor items={customItems} setItems={setCustomItems} />

        {/* Scénario + objet + corps de l'email */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message envoyé au destinataire</h4>

          <div className="mb-3">
            <label className="block text-xs text-gray-600 mb-1">Type de demande</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'embauche',   label: 'Embauche',            desc: 'Constitution du dossier initial' },
                { key: 'complement', label: 'Document complémentaire', desc: 'Pièce ponctuelle (mutuelle conjoint, etc.)' },
                { key: 'mise_a_jour', label: 'Mise à jour annuelle', desc: 'Renouvellement de pièces' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => handleChangeScenario(opt.key)}
                  className={`px-3 py-1.5 text-xs rounded border ${scenario === opt.key ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  title={opt.desc}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {(subjectEdited || bodyEdited) && (
              <p className="text-[10px] text-amber-600 mt-1">
                ⚠️ Texte personnalisé — changer de type ne le remplacera plus.
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={() => {
                    setSubjectEdited(false); setBodyEdited(false);
                    const p = presets[scenario] || {};
                    setEmailSubject(p.subject || '');
                    setEmailBody(p.body || '');
                  }}
                >
                  Restaurer le modèle
                </button>
              </p>
            )}
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-600 mb-1">Objet</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => { setEmailSubject(e.target.value); setSubjectEdited(true); }}
              className={inputCls}
              placeholder="Documents à fournir — Prénom"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Corps du message</label>
            <textarea
              value={emailBody}
              onChange={(e) => { setEmailBody(e.target.value); setBodyEdited(true); }}
              className={inputCls + ' font-mono text-[13px] leading-relaxed min-h-[200px]'}
              rows={10}
              placeholder="Bonjour, ..."
            />
            <p className="text-[10px] text-gray-400 mt-1">
              {`Si tu écris `}<code className="bg-gray-100 px-1 rounded text-[10px]">{'{{portalUrl}}'}</code>{` dans le texte, il sera remplacé par le lien sécurisé. Sinon le lien est ajouté automatiquement par le modèle.`}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Éditeur de documents personnalisés (one-shot) ──────────────────────

function CustomItemsEditor({ items, setItems }) {
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';
  const labelRefs = React.useRef({}); // refs indexées par idx pour auto-focus

  const addItem = () => {
    setItems((prev) => {
      const nextIdx = prev.length;
      const next = [...prev, { label: '', required: true, category: '' }];
      // Auto-focus le nouveau champ libellé après render
      setTimeout(() => {
        const ref = labelRefs.current[nextIdx];
        if (ref) { ref.focus(); ref.select?.(); }
      }, 50);
      return next;
    });
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" /> Documents personnalisés (ponctuels)
          </h4>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Pour des pièces qui n'ont pas vocation à être réutilisées (ex : attestation mutuelle du conjoint).
          </p>
        </div>
        <button
          type="button"
          onClick={addItem}
          className="text-xs text-primary hover:text-primary-dark flex items-center gap-1 px-2 py-1 border border-primary/30 rounded hover:bg-primary/5"
        >
          <PlusCircle size={13} /> Ajouter un document personnalisé
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400 italic px-1 py-2 border border-dashed border-gray-200 rounded">
          Aucun pour l'instant. Clique sur « Ajouter un document personnalisé » pour saisir un libellé libre.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="bg-amber-50/50 border border-amber-200 rounded-lg p-3">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-7">
                  <label className="block text-[10px] text-gray-600 mb-0.5">Libellé du document <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    ref={(el) => { labelRefs.current[idx] = el; }}
                    value={it.label}
                    onChange={(e) => updateItem(idx, { label: e.target.value })}
                    className={inputCls}
                    placeholder="Ex : Attestation mutuelle du conjoint"
                    autoComplete="off"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-[10px] text-gray-600 mb-0.5">Catégorie</label>
                  <input
                    type="text"
                    value={it.category || ''}
                    onChange={(e) => updateItem(idx, { category: e.target.value })}
                    className={inputCls}
                    placeholder="Ex : Famille"
                    autoComplete="off"
                  />
                </div>
                <div className="col-span-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => updateItem(idx, { required: !it.required })}
                    className={`flex-1 text-[10px] px-2 py-1.5 rounded ${it.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}
                    title={it.required ? 'Obligatoire — clic pour rendre optionnel' : 'Optionnel — clic pour rendre obligatoire'}
                  >
                    {it.required ? 'Oblig.' : 'Opt.'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer cette ligne"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Modale "Ajouter une pièce" à une demande existante ─────────────────

function AddItemsModal({ requestId, docTypes, existingItems, onClose, onAdded }) {
  const toast = useToast();
  const [selected, setSelected] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const existingTypeIds = new Set(existingItems.filter((it) => it.documentTypeId).map((it) => it.documentTypeId));
  const availableTypes = docTypes.filter((t) => !existingTypeIds.has(t.id));

  const grouped = availableTypes.reduce((acc, t) => {
    const cat = t.category || "Autres";
    (acc[cat] ||= []).push(t);
    return acc;
  }, {});

  const cleanedCustom = customItems
    .map((it) => ({ ...it, label: String(it.label || "").trim() }))
    .filter((it) => it.label.length > 0);

  const checkedIds = Object.entries(selected).filter(([, v]) => v.checked).map(([id]) => id);

  const handleSubmit = async () => {
    if (checkedIds.length === 0 && cleanedCustom.length === 0) {
      toast.error("Sélectionne au moins un document ou ajoute un document personnalisé");
      return;
    }
    setSubmitting(true);
    try {
      const requiredMap = {};
      checkedIds.forEach((id) => { requiredMap[id] = selected[id]?.required !== false; });
      await addDocumentRequestItems(requestId, {
        documentTypeIds: checkedIds,
        required: requiredMap,
        customItems: cleanedCustom,
      });
      toast.success("Pièce(s) ajoutée(s) à la demande");
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur ajout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Ajouter des pièces à la demande"
      size="xl"
      footer={
        <>
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-60 flex items-center gap-2"
          >
            {submitting ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
            {submitting ? "Ajout..." : "Ajouter à la demande"}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          Les pièces ajoutées apparaîtront immédiatement dans le portail candidat. Pas besoin de renvoyer un email — il les verra à son prochain rafraîchissement.
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documents du référentiel</h4>
          {availableTypes.length === 0 ? (
            <div className="text-xs text-gray-400 italic">Tous les types disponibles sont déjà dans la demande.</div>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-600">{cat}</div>
                  {items.map((t) => {
                    const sel = selected[t.id] || {};
                    return (
                      <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={!!sel.checked}
                          onChange={() => setSelected((s) => ({ ...s, [t.id]: { ...s[t.id], checked: !s[t.id]?.checked, required: s[t.id]?.required ?? true } }))}
                        />
                        <div className="flex-1 min-w-0 text-gray-800 truncate">{t.name}</div>
                        <label
                          className={`text-[10px] px-2 py-0.5 rounded cursor-pointer ${sel.required ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (sel.checked) setSelected((s) => ({ ...s, [t.id]: { ...s[t.id], required: !s[t.id]?.required } })); }}
                        >
                          {sel.required ? "Obligatoire" : "Optionnel"}
                        </label>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <CustomItemsEditor items={customItems} setItems={setCustomItems} />
      </div>
    </Modal>
  );
}
